import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  fetchUserById,
  fetchUserProducts,
  fetchUserServices,
  fetchUserSales,
  fetchUserServiceOrders,
  fetchUserCategories,
  fetchUserCostumers,
  fetchUserPayMethods,
  fetchUserFinancialSummary,
  deleteUserSale,
  deleteUserCompletely
} from '../services/firebase';
import { User } from '../types';
import { theme } from '../styles/theme';
import { doc, updateDoc } from 'firebase/firestore';
import { db } from '../services/firebase';
import PhoneIcon from '@mui/icons-material/Phone';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import SaveIcon from '@mui/icons-material/Save';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import PhoneAndroidIcon from '@mui/icons-material/PhoneAndroid';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import WarningIcon from '@mui/icons-material/Warning';
import VisibilityIcon from '@mui/icons-material/Visibility';
import useMediaQuery from '@mui/material/useMediaQuery';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import HandymanIcon from '@mui/icons-material/Handyman';
import BuildIcon from '@mui/icons-material/Build';

const UserDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const isMobile = useMediaQuery('(max-width:768px)');
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [isEditingDate, setIsEditingDate] = useState(false);
  const [newExpirationDate, setNewExpirationDate] = useState('');
  const [isEditingLimit, setIsEditingLimit] = useState(false);
  const [newLimitDevices, setNewLimitDevices] = useState(0);
  const [productsCount, setProductsCount] = useState(0);
  const [servicesCount, setServicesCount] = useState(0);
  const [salesCount, setSalesCount] = useState(0);
  const [salesTotal, setSalesTotal] = useState(0);
  const [serviceOrdersCount, setServiceOrdersCount] = useState(0);
  const [serviceOrdersTotal, setServiceOrdersTotal] = useState(0);
  const [categoriesCount, setCategoriesCount] = useState(0);
  const [costumersCount, setCostumersCount] = useState(0);
  const [payMethodsCount, setPayMethodsCount] = useState(0);
  const [payableAccountsCount, setPayableAccountsCount] = useState(0);
  const [receivableAccountsCount, setReceivableAccountsCount] = useState(0);
  const [panelMode, setPanelMode] = useState<'empty' | 'products' | 'services' | 'costumers' | 'sales' | 'serviceOrders'>('empty');
  const [panelLoading, setPanelLoading] = useState(false);
  const [panelError, setPanelError] = useState('');
  const [productsDetails, setProductsDetails] = useState<any[]>([]);
  const [servicesDetails, setServicesDetails] = useState<any[]>([]);
  const [costumersDetails, setCostumersDetails] = useState<any[]>([]);
  const [salesDetails, setSalesDetails] = useState<any[]>([]);
  const [serviceOrdersDetails, setServiceOrdersDetails] = useState<any[]>([]);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showSaleDeleteModal, setShowSaleDeleteModal] = useState(false);
  const [saleToDelete, setSaleToDelete] = useState<any | null>(null);
  const [deletingSale, setDeletingSale] = useState(false);
  const navigate = useNavigate();
  const mobilePanelVisible = isMobile && panelMode !== 'empty';

  useEffect(() => {
    const loadUser = async () => {
      if (!id) return;
      
      try {
        const data = await fetchUserById(id);
        setUser(data as User);
        const userData = data as User;
        setNewExpirationDate(userData.expirationDate || '');
        setNewLimitDevices(userData.limitDevices || 0);
        
        // Buscar estatísticas do usuário
        const products = await fetchUserProducts(id);
        const services = await fetchUserServices(id);
        const sales = await fetchUserSales(id);
        const serviceOrders = await fetchUserServiceOrders(id);
        const categories = await fetchUserCategories(id);
        const costumers = await fetchUserCostumers(id);
        const payMethods = await fetchUserPayMethods(id);
        const financialSummary = await fetchUserFinancialSummary(id);
        setProductsCount(products.length);
        setServicesCount(services.length);
        setSalesCount(sales.length);
        setSalesTotal(sales.reduce((sum: number, sale: any) => sum + getSaleTotal(sale), 0));
        setServiceOrdersCount(serviceOrders.length);
        setServiceOrdersTotal(serviceOrders.reduce((sum: number, order: any) => sum + getServiceOrderTotal(order), 0));
        setCategoriesCount(categories.length);
        setCostumersCount(costumers.length);
        setPayMethodsCount(payMethods.length);
        setPayableAccountsCount(financialSummary.payableCount);
        setReceivableAccountsCount(financialSummary.receivableCount);
      } catch (err: any) {
        setError('Usuário não encontrado');
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      console.log('📄 UserDetail recebeu UID da URL:', id);
      loadUser();
    }
  }, [id]);

  const handleBack = () => {
    navigate('/home');
  };

  const handleShowProductsDetails = async () => {
    if (!id) return;

    setPanelMode('products');
    setPanelLoading(true);
    setPanelError('');

    try {
      const products = await fetchUserProducts(id);
      setProductsDetails(products as any[]);
    } catch (err) {
      console.error('Erro ao carregar produtos:', err);
      setPanelError('Erro ao carregar produtos');
      setProductsDetails([]);
    } finally {
      setPanelLoading(false);
    }
  };

  const handleShowServicesDetails = async () => {
    if (!id) return;

    setPanelMode('services');
    setPanelLoading(true);
    setPanelError('');

    try {
      const services = await fetchUserServices(id);
      setServicesDetails(services as any[]);
    } catch (err) {
      console.error('Erro ao carregar serviços:', err);
      setPanelError('Erro ao carregar serviços');
      setServicesDetails([]);
    } finally {
      setPanelLoading(false);
    }
  };

  const handleShowCostumersDetails = async () => {
    if (!id) return;

    setPanelMode('costumers');
    setPanelLoading(true);
    setPanelError('');

    try {
      const costumers = await fetchUserCostumers(id);
      setCostumersDetails(costumers as any[]);
    } catch (err) {
      console.error('Erro ao carregar clientes:', err);
      setPanelError('Erro ao carregar clientes');
      setCostumersDetails([]);
    } finally {
      setPanelLoading(false);
    }
  };

  const handleShowSalesDetails = async () => {
    if (!id) return;

    setPanelMode('sales');
    setPanelLoading(true);
    setPanelError('');

    try {
      const sales = await fetchUserSales(id);
      setSalesDetails(sortSalesByDate(sales as any[]));
    } catch (err) {
      console.error('Erro ao carregar vendas:', err);
      setPanelError('Erro ao carregar vendas');
      setSalesDetails([]);
    } finally {
      setPanelLoading(false);
    }
  };

  const handleAskDeleteSale = (sale: any) => {
    setSaleToDelete(sale);
    setShowSaleDeleteModal(true);
  };

  const handleDeleteSale = async () => {
    if (!id || !saleToDelete?.id || deletingSale) return;

    setDeletingSale(true);

    try {
      await deleteUserSale(id, saleToDelete.id, Array.isArray(saleToDelete.idFinance) ? saleToDelete.idFinance : []);

      const sales = await fetchUserSales(id);
      setSalesDetails(sortSalesByDate(sales as any[]));
      setSalesCount(sales.length);
      setSalesTotal(sales.reduce((sum: number, sale: any) => sum + getSaleTotal(sale), 0));
      setShowSaleDeleteModal(false);
      setSaleToDelete(null);
    } catch (err) {
      console.error('Erro ao excluir venda:', err);
      setPanelError('Erro ao excluir venda');
    } finally {
      setDeletingSale(false);
    }
  };

  const handleShowServiceOrdersDetails = async () => {
    if (!id) return;

    setPanelMode('serviceOrders');
    setPanelLoading(true);
    setPanelError('');

    try {
      const serviceOrders = await fetchUserServiceOrders(id);
      setServiceOrdersDetails(serviceOrders as any[]);
    } catch (err) {
      console.error('Erro ao carregar ordens de serviço:', err);
      setPanelError('Erro ao carregar ordens de serviço');
      setServiceOrdersDetails([]);
    } finally {
      setPanelLoading(false);
    }
  };

  const formatCurrency = (value?: number) => {
    if (typeof value !== 'number') return 'N/A';
    return value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
  };

  const getSaleTotal = (sale: any) => Number(sale?.cart?.totalValue) || 0;

  const getServiceOrderTotal = (order: any) => {
    if (typeof order?.totalValue === 'number') return order.totalValue;

    const servicesTotal = (Array.isArray(order?.services) ? order.services : [])
      .reduce((sum: number, item: any) => sum + (Number(item?.totalValue) || 0), 0);
    const productsTotal = (Array.isArray(order?.products) ? order.products : [])
      .reduce((sum: number, item: any) => sum + (Number(item?.totalValue) || 0), 0);

    return servicesTotal + productsTotal;
  };

  const getDateTimestamp = (value: any) => {
    if (value?.toDate) return value.toDate().getTime();
    const timestamp = new Date(value || 0).getTime();
    return Number.isNaN(timestamp) ? 0 : timestamp;
  };

  const sortSalesByDate = (sales: any[]) => [...sales].sort(
    (a, b) => getDateTimestamp(b.date || b.createdAt) - getDateTimestamp(a.date || a.createdAt)
  );

  const formatOptionalDate = (dateString?: string) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    if (Number.isNaN(date.getTime())) return 'N/A';

    return date.toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatFieldLabel = (key: string) => {
    const normalized = key
      .replace(/_/g, ' ')
      .replace(/([a-z])([A-Z])/g, '$1 $2')
      .trim();
    return normalized.charAt(0).toUpperCase() + normalized.slice(1);
  };

  const formatFieldValue = (key: string, value: any) => {
    if (value === undefined || value === null || value === '') return 'N/A';
    if (key.toLowerCase().includes('date') || key.endsWith('At')) {
      return formatOptionalDate(String(value));
    }
    if (typeof value === 'boolean') return value ? 'Sim' : 'Não';
    if (typeof value === 'object') return JSON.stringify(value);
    return String(value);
  };

  const getSaleItemTitle = (item: any, index: number) => {
    return item?.product?.name || item?.name || item?.productName || item?.title || item?.id || `Item ${index + 1}`;
  };

  const getServiceItemTitle = (item: any, index: number) => {
    return item?.name || item?.service?.name || item?.serviceName || item?.title || item?.serviceId || item?.id || `Serviço ${index + 1}`;
  };

  const isExpired = (expirationDate: string) => {
    const expDate = new Date(expirationDate);
    const today = new Date();
    return expDate < today;
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatDateInput = (dateString: string) => {
    const date = new Date(dateString);
    return date.toISOString().slice(0, 16);
  };

  const handleSaveExpirationDate = async () => {
    if (!id || !user) return;
    
    setSaving(true);
    try {
      const userRef = doc(db, 'user', id);
      await updateDoc(userRef, {
        expirationDate: newExpirationDate
      });
      
      setUser({ ...user, expirationDate: newExpirationDate });
      setIsEditingDate(false);
      alert('Data de expiração atualizada com sucesso!');
    } catch (err) {
      console.error('Erro ao atualizar data:', err);
      alert('Erro ao atualizar data de expiração');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveLimitDevices = async () => {
    if (!id || !user) return;
    
    // Validação: se está reduzindo o limite, verificar se todos os slots estão preenchidos
    const currentDevices = user.devices?.length || 0;
    if (newLimitDevices < (user.limitDevices || 0) && currentDevices > newLimitDevices) {
      alert(`Não é possível reduzir o limite para ${newLimitDevices}. Existem ${currentDevices} dispositivos cadastrados. Por favor, exclua alguns dispositivos primeiro.`);
      return;
    }
    
    setSaving(true);
    try {
      const userRef = doc(db, 'user', id);
      await updateDoc(userRef, {
        limitDevices: newLimitDevices
      });
      
      setUser({ ...user, limitDevices: newLimitDevices });
      setIsEditingLimit(false);
      alert('Limite de dispositivos atualizado com sucesso!');
    } catch (err) {
      console.error('Erro ao atualizar limite:', err);
      alert('Erro ao atualizar limite de dispositivos');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteDevice = async (deviceId: string) => {
    if (!id || !user) return;
    
    if (!window.confirm('Deseja realmente excluir este dispositivo?')) {
      return;
    }
    
    setSaving(true);
    try {
      const updatedDevices = user.devices?.filter(device => device.id !== deviceId) || [];
      
      const userRef = doc(db, 'user', id);
      await updateDoc(userRef, {
        devices: updatedDevices
      });
      
      setUser({ ...user, devices: updatedDevices });
      alert('Dispositivo excluído com sucesso!');
    } catch (err) {
      console.error('Erro ao excluir dispositivo:', err);
      alert('Erro ao excluir dispositivo');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteUser = async () => {
    if (!id || !user) return;
    
    setShowDeleteModal(false);
    setDeleting(true);
    
    try {
      // Excluir completamente o usuário
      await deleteUserCompletely(id);
      
      // Mostrar mensagem de sucesso
      alert('Cliente excluído completamente com sucesso!');
      
      // Redirecionar para a home
      navigate('/home');
    } catch (err: any) {
      console.error('Erro ao excluir cliente:', err);
      const errorMessage = err?.message || 'Erro desconhecido ao excluir cliente';
      alert(`Erro ao excluir cliente: ${errorMessage}\n\nVerifique o console para mais detalhes.`);
      setDeleting(false);
    }
  };

  if (loading) {
    return (
      <div style={styles.loadingContainer}>
        <div style={styles.loading}>Carregando...</div>
      </div>
    );
  }

  if (error || !user) {
    return (
      <div style={styles.container}>
        <div style={styles.errorContainer}>
          <p style={styles.error}>{error}</p>
          <button style={styles.errorBackButton} onClick={handleBack}>
            ← Voltar para Home
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <header style={styles.header}>
        <div style={styles.headerContent}>
          <button style={styles.backButton} onClick={handleBack} title="Voltar">
            <ArrowBackIcon style={{ fontSize: 24 }} />
          </button>
          <h1 style={styles.title}>Detalhes do Cliente</h1>
        </div>
      </header>
      <div style={{ ...styles.content, ...(isMobile ? styles.contentMobile : {}) }}>
        <div style={{ ...styles.detailLayout, ...(isMobile ? styles.detailLayoutMobile : {}) }}>
          {(!isMobile || !mobilePanelVisible) && (
            <div style={{ ...styles.leftColumn, ...(isMobile ? styles.leftColumnMobile : {}) }}>
            <div style={styles.card}>
              <div style={styles.avatarContainer}>
            {user.photoUrl ? (
              <img src={user.photoUrl} alt={user.businessName} style={styles.avatarImage} />
            ) : (
              <div style={styles.avatar}>
                {user.businessName ? user.businessName.charAt(0).toUpperCase() : 'U'}
              </div>
            )}
              </div>
              <h2 style={styles.userName}>{user.businessName || 'Nome não informado'}</h2>
              <p style={styles.userId}>ID: {user.id}</p>
              <p style={styles.userUid}>UID: {user.uid || id}</p>
          
              <div style={styles.statusBadge}>
                <span style={isExpired(user.expirationDate) ? styles.statusExpired : styles.statusActive}>
                  {isExpired(user.expirationDate) ? '🔴 EXPIRADO' : '🟢 ATIVO'}
                </span>
              </div>

          <div style={styles.infoSection}>
            <h3 style={styles.sectionTitle}>Informações de Contato</h3>
            <div style={styles.infoItem}>
              <span style={styles.label}>Email:</span>
              <span style={styles.value}>{user.mail || 'Não informado'}</span>
            </div>
            <div style={styles.infoItem}>
              <span style={styles.label}><PhoneIcon style={{ fontSize: 18, marginRight: 5 }} /> Telefone:</span>
              <span style={styles.value}>{user.phone || 'Não informado'}</span>
            </div>
            <div style={styles.infoItem}>
              <span style={styles.label}>Documento:</span>
              <span style={styles.value}>{user.document || 'Não informado'}</span>
            </div>
          </div>

          <div style={styles.infoSection}>
            <h3 style={styles.sectionTitle}><CalendarMonthIcon style={{ fontSize: 20, marginRight: 8 }} /> Informações de Assinatura</h3>
            <div style={styles.infoItem}>
              <span style={styles.label}>Data de Ativação:</span>
              <span style={styles.value}>{formatDate(user.activationDate)}</span>
            </div>
            
            <div style={styles.infoItem}>
              <span style={styles.label}>Data de Expiração:</span>
              <div style={styles.dateEditContainer}>
                {isEditingDate ? (
                  <div style={styles.editDateBox}>
                    <input
                      type="datetime-local"
                      value={formatDateInput(newExpirationDate)}
                      onChange={(e) => setNewExpirationDate(new Date(e.target.value).toISOString())}
                      style={styles.dateInput}
                    />
                    <div style={styles.editButtons}>
                      <button
                        onClick={handleSaveExpirationDate}
                        disabled={saving}
                        style={styles.saveButton}
                      >
                        <SaveIcon style={{ fontSize: 18, marginRight: 5 }} />
                        {saving ? 'Salvando...' : 'Salvar'}
                      </button>
                      <button
                        onClick={() => {
                          setIsEditingDate(false);
                          setNewExpirationDate(user.expirationDate);
                        }}
                        style={styles.cancelButton}
                        disabled={saving}
                      >
                        ❌ Cancelar
                      </button>
                    </div>
                  </div>
                ) : (
                  <div style={styles.dateDisplayBox}>
                    <span style={styles.value}>{formatDate(user.expirationDate)}</span>
                    <button
                      onClick={() => setIsEditingDate(true)}
                      style={styles.editButton}
                      title="Editar"
                    >
                      <EditIcon style={{ fontSize: 20 }} />
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Estatísticas */}
          <div style={styles.infoSection}>
            <h3 style={styles.sectionTitle}>Estatísticas</h3>
            <div style={styles.infoItem}>
              <div style={styles.statHeaderRow}>
                <span style={styles.label}>Produtos Cadastrados:</span>
                <button
                  type="button"
                  onClick={handleShowProductsDetails}
                  style={styles.detailIconButton}
                  title="Detalhar produtos"
                >
                  <VisibilityIcon style={{ fontSize: 18 }} />
                </button>
              </div>
              <span style={styles.value}>{productsCount}</span>
            </div>
            <div style={styles.infoItem}>
              <div style={styles.statHeaderRow}>
                <span style={styles.label}><BuildIcon style={{ fontSize: 18, marginRight: 5 }} /> Serviços Cadastrados:</span>
                <button
                  type="button"
                  onClick={handleShowServicesDetails}
                  style={styles.detailIconButton}
                  title="Detalhar serviços"
                >
                  <VisibilityIcon style={{ fontSize: 18 }} />
                </button>
              </div>
              <span style={styles.value}>{servicesCount}</span>
            </div>
            <div style={styles.infoItem}>
              <div style={styles.statHeaderRow}>
                <span style={styles.label}><AttachMoneyIcon style={{ fontSize: 18, marginRight: 5 }} /> Vendas Realizadas:</span>
                <button
                  type="button"
                  onClick={handleShowSalesDetails}
                  style={styles.detailIconButton}
                  title="Detalhar vendas"
                >
                  <VisibilityIcon style={{ fontSize: 18 }} />
                </button>
              </div>
              <span style={styles.value}>{salesCount} ({formatCurrency(salesTotal)})</span>
            </div>
            <div style={styles.infoItem}>
              <div style={styles.statHeaderRow}>
                <span style={styles.label}><HandymanIcon style={{ fontSize: 18, marginRight: 5 }} /> Ordens de Serviço (OS):</span>
                <button
                  type="button"
                  onClick={handleShowServiceOrdersDetails}
                  style={styles.detailIconButton}
                  title="Detalhar ordens de serviço"
                >
                  <VisibilityIcon style={{ fontSize: 18 }} />
                </button>
              </div>
              <span style={styles.value}>{serviceOrdersCount} ({formatCurrency(serviceOrdersTotal)})</span>
            </div>
            <div style={styles.infoItem}>
              <span style={styles.label}>Categorias:</span>
              <span style={styles.value}>{categoriesCount}</span>
            </div>
            <div style={styles.infoItem}>
              <div style={styles.statHeaderRow}>
                <span style={styles.label}>Clientes:</span>
                <button
                  type="button"
                  onClick={handleShowCostumersDetails}
                  style={styles.detailIconButton}
                  title="Detalhar clientes"
                >
                  <VisibilityIcon style={{ fontSize: 18 }} />
                </button>
              </div>
              <span style={styles.value}>{costumersCount}</span>
            </div>
            <div style={styles.infoItem}>
              <span style={styles.label}>Formas de Pagamento:</span>
              <span style={styles.value}>{payMethodsCount}</span>
            </div>
            <div style={styles.infoItem}>
              <span style={styles.label}>Contas Financeiras Criadas:</span>
              <div style={styles.financeCountersRow}>
                <div style={styles.financeCounterChip}>
                  <TrendingDownIcon style={styles.financePayableIcon} />
                  {!isMobile && <span style={styles.financeCounterLabel}>A pagar:</span>}
                  <span style={isMobile ? styles.financeCounterValueMobile : styles.financeCounterValue}>{payableAccountsCount}</span>
                </div>
                <div style={styles.financeCounterChip}>
                  <TrendingUpIcon style={styles.financeReceivableIcon} />
                  {!isMobile && <span style={styles.financeCounterLabel}>A receber:</span>}
                  <span style={isMobile ? styles.financeCounterValueMobile : styles.financeCounterValue}>{receivableAccountsCount}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Dispositivos */}
          <div style={styles.infoSection}>
            <h3 style={styles.sectionTitle}><PhoneAndroidIcon style={{ fontSize: 20, marginRight: 8 }} /> Dispositivos</h3>
            <div style={styles.infoItem}>
              <span style={styles.label}>Limite de Dispositivos:</span>
              <div style={styles.dateEditContainer}>
                {isEditingLimit ? (
                  <div style={styles.editDateBox}>
                    <input
                      type="number"
                      min="0"
                      value={newLimitDevices}
                      onChange={(e) => setNewLimitDevices(parseInt(e.target.value) || 0)}
                      style={styles.dateInput}
                    />
                    <div style={styles.editButtons}>
                      <button
                        onClick={handleSaveLimitDevices}
                        disabled={saving}
                        style={styles.saveButton}
                      >
                        <SaveIcon style={{ fontSize: 18, marginRight: 5 }} />
                        {saving ? 'Salvando...' : 'Salvar'}
                      </button>
                      <button
                        onClick={() => {
                          setNewLimitDevices(user.limitDevices || 0);
                          setIsEditingLimit(false);
                        }}
                        disabled={saving}
                        style={styles.cancelButton}
                      >
                        Cancelar
                      </button>
                    </div>
                  </div>
                ) : (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <span style={styles.value}>{user.limitDevices || 0}</span>
                    <button
                      onClick={() => setIsEditingLimit(true)}
                      style={styles.editButton}
                      title="Editar"
                    >
                      <EditIcon style={{ fontSize: 20 }} />
                    </button>
                  </div>
                )}
              </div>
            </div>
            <div style={styles.infoItem}>
              <span style={styles.label}>Dispositivos Cadastrados:</span>
              <span style={styles.value}>
                {user.devices?.length || 0} / {user.limitDevices || 0}
              </span>
            </div>
          </div>

          {user.devices && user.devices.length > 0 && (
            <div style={styles.infoSection}>
              <h3 style={styles.sectionTitle}>Lista de Dispositivos</h3>
              {user.devices.map((device: any, index: number) => (
                <div key={index} style={styles.deviceCard}>
                  <div style={styles.deviceHeader}>
                    <span style={styles.deviceNumber}>Dispositivo #{index + 1}</span>
                    <button
                      onClick={() => handleDeleteDevice(device.id)}
                      disabled={saving}
                      style={styles.deleteDeviceButton}
                      title="Excluir dispositivo"
                    >
                      <DeleteIcon style={{ fontSize: 20 }} />
                    </button>
                  </div>
                  <div style={styles.deviceInfo}>
                    <div style={styles.deviceRow}>
                      <span style={styles.deviceLabel}>ID:</span>
                      <span style={styles.deviceValue}>{device.id || 'N/A'}</span>
                    </div>
                    <div style={styles.deviceRow}>
                      <span style={styles.deviceLabel}>Marca:</span>
                      <span style={styles.deviceValue}>{device.marca || 'N/A'}</span>
                    </div>
                    <div style={styles.deviceRow}>
                      <span style={styles.deviceLabel}><PhoneAndroidIcon style={{ fontSize: 16, marginRight: 5 }} /> Modelo:</span>
                      <span style={styles.deviceValue}>{device.modelo || 'N/A'}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

              {/* Botão de Excluir Conta */}
              <div style={styles.dangerZone}>
                <h3 style={styles.dangerZoneTitle}>Zona de Perigo</h3>
                <p style={styles.dangerZoneDescription}>
                  Ao excluir a conta, todos os dados do cliente serão permanentemente removidos, incluindo:
                  produtos, vendas, métodos de pagamento e dispositivos cadastrados.
                </p>
                <button
                  onClick={() => setShowDeleteModal(true)}
                  disabled={deleting}
                  style={styles.deleteAccountButton}
                >
                  <DeleteIcon style={{ fontSize: 18, marginRight: 8 }} />
                  {deleting ? 'Excluindo...' : 'Excluir Conta Permanentemente'}
                </button>
              </div>
            </div>
          </div>
          )}

          {(!isMobile || mobilePanelVisible) && (
          <aside style={{ ...styles.rightPanel, ...(isMobile ? styles.rightPanelMobile : {}) }}>
            {panelMode === 'products' ? (
              <div style={styles.rightPanelContent}>
                <div style={styles.rightPanelHeader}>
                  {isMobile && (
                    <button
                      type="button"
                      onClick={() => setPanelMode('empty')}
                      style={styles.panelBackButton}
                      title="Voltar"
                    >
                      <ArrowBackIcon style={{ fontSize: 18 }} />
                    </button>
                  )}
                  <h3 style={styles.rightPanelSectionTitle}>Produtos ({productsDetails.length})</h3>
                </div>
                {panelLoading ? (
                  <p style={styles.rightPanelStateText}>Carregando produtos...</p>
                ) : panelError ? (
                  <p style={styles.rightPanelErrorText}>{panelError}</p>
                ) : productsDetails.length === 0 ? (
                  <p style={styles.rightPanelStateText}>Sem produtos cadastrados</p>
                ) : (
                  <div style={styles.productList}>
                    {productsDetails.map((product, index) => (
                      <div key={product.id || index} style={styles.productItem}>
                        <div style={styles.productTopRow}>
                          {product.photoUrl ? (
                            <img src={product.photoUrl} alt={product.name || 'Produto'} style={styles.productThumb} />
                          ) : (
                            <div style={styles.productThumbPlaceholder}>🧾</div>
                          )}
                          <div style={styles.productMainInfo}>
                            <p style={styles.productName}>{product.name || 'Sem nome'}</p>
                            <p style={styles.productPrice}>{formatCurrency(product.price)}</p>
                          </div>
                        </div>

                        <div style={styles.productMetaGrid}>
                          <p style={styles.productMeta}><strong>ID:</strong> {product.id || 'N/A'}</p>
                          <p style={styles.productMeta}><strong>Custo:</strong> {formatCurrency(product.coust)}</p>
                          <p style={styles.productMeta}><strong>Atacado:</strong> {product.wholesale === undefined ? 'N/A' : product.wholesale ? 'Sim' : 'Não'}</p>
                          <p style={styles.productMeta}><strong>Qnt:</strong> {product.qnt ?? 'N/A'}</p>
                          <p style={styles.productMeta}><strong>Desconto:</strong> {product.discount ?? 'N/A'}</p>
                          <p style={styles.productMeta}>
                            <strong>Estoque:</strong>{' '}
                            <span
                              style={{
                                color:
                                  typeof product.stock === 'number'
                                    ? product.stock < 0
                                      ? '#d32f2f'
                                      : product.stock > 0
                                        ? '#2e7d32'
                                        : theme.colors.text
                                    : theme.colors.text,
                                fontWeight: typeof product.stock === 'number' ? '700' : '400',
                              }}
                            >
                              {product.stock ?? 'N/A'}
                            </span>
                          </p>
                          <p style={styles.productMeta}><strong>Criado em:</strong> {formatOptionalDate(product.createdAt)}</p>
                          <p style={styles.productMeta}><strong>Atualizado em:</strong> {formatOptionalDate(product.updatedAt)}</p>
                          <p style={styles.productMeta}><strong>Menu ativo:</strong> {product.menuActive === undefined ? 'N/A' : product.menuActive ? 'Sim' : 'Não'}</p>
                          <p style={styles.productMeta}><strong>Destaque:</strong> {product.highlights === undefined ? 'N/A' : product.highlights ? 'Sim' : 'Não'}</p>
                          <p style={styles.productMeta}><strong>Categoria:</strong> {product.category?.name || 'N/A'}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ) : panelMode === 'services' ? (
              <div style={styles.rightPanelContent}>
                <div style={styles.rightPanelHeader}>
                  {isMobile && (
                    <button
                      type="button"
                      onClick={() => setPanelMode('empty')}
                      style={styles.panelBackButton}
                      title="Voltar"
                    >
                      <ArrowBackIcon style={{ fontSize: 18 }} />
                    </button>
                  )}
                  <h3 style={styles.rightPanelSectionTitle}>Serviços ({servicesDetails.length})</h3>
                </div>
                {panelLoading ? (
                  <p style={styles.rightPanelStateText}>Carregando serviços...</p>
                ) : panelError ? (
                  <p style={styles.rightPanelErrorText}>{panelError}</p>
                ) : servicesDetails.length === 0 ? (
                  <p style={styles.rightPanelStateText}>Sem serviços cadastrados</p>
                ) : (
                  <div style={styles.productList}>
                    {servicesDetails.map((service, index) => (
                      <div key={service.id || index} style={styles.productItem}>
                        <div style={styles.productTopRow}>
                          <div style={styles.productThumbPlaceholder}>🔧</div>
                          <div style={styles.productMainInfo}>
                            <p style={styles.productName}>{service.name || 'Sem nome'}</p>
                            <p style={styles.productPrice}>{formatCurrency(service.price)}</p>
                          </div>
                        </div>

                        <div style={styles.productMetaGrid}>
                          <p style={styles.productMeta}><strong>ID:</strong> {service.id || 'N/A'}</p>
                          <p style={styles.productMeta}><strong>Preço:</strong> {formatCurrency(service.price)}</p>
                          <p style={styles.productMeta}><strong>Criado em:</strong> {formatOptionalDate(service.createdAt)}</p>
                          <p style={styles.productMeta}><strong>Atualizado em:</strong> {formatOptionalDate(service.updatedAt)}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ) : panelMode === 'sales' ? (
              <div style={styles.rightPanelContent}>
                <div style={styles.rightPanelHeader}>
                  {isMobile && (
                    <button
                      type="button"
                      onClick={() => setPanelMode('empty')}
                      style={styles.panelBackButton}
                      title="Voltar"
                    >
                      <ArrowBackIcon style={{ fontSize: 18 }} />
                    </button>
                  )}
                  <h3 style={styles.rightPanelSectionTitle}>Vendas ({salesDetails.length})</h3>
                </div>
                {panelLoading ? (
                  <p style={styles.rightPanelStateText}>Carregando vendas...</p>
                ) : panelError ? (
                  <p style={styles.rightPanelErrorText}>{panelError}</p>
                ) : salesDetails.length === 0 ? (
                  <p style={styles.rightPanelStateText}>Sem vendas cadastradas</p>
                ) : (
                  <div style={styles.productList}>
                    {sortSalesByDate(salesDetails).map((sale, index) => {
                      const cart = sale?.cart || {};
                      const cartItems = Array.isArray(cart.sale) ? cart.sale : [];
                      const costumer = cart?.costumer || {};
                      const saleTitle = `Venda para ${costumer.name || costumer.businessName || 'Cliente Anônimo'}`;

                      return (
                        <div key={sale.id || index} style={styles.productItem}>
                          <div style={styles.saleTopRow}>
                            <div style={styles.productTopRow}>
                              <div style={styles.productThumbPlaceholder}>💰</div>
                              <div style={styles.productMainInfo}>
                                <p style={styles.productName}>{saleTitle}</p>
                                <p style={styles.productPrice}>{formatCurrency(cart.totalValue)}</p>
                              </div>
                            </div>
                            <button
                              type="button"
                              onClick={() => handleAskDeleteSale(sale)}
                              style={styles.deleteSaleButton}
                              title="Excluir venda"
                              aria-label={`Excluir ${saleTitle}`}
                            >
                              <DeleteIcon style={{ fontSize: 18 }} />
                            </button>
                          </div>

                          <div style={styles.productMetaGrid}>
                            <p style={styles.productMeta}><strong>ID:</strong> {sale.id || 'N/A'}</p>
                            <p style={styles.productMeta}><strong>Data:</strong> {formatOptionalDate(sale.date)}</p>
                            <p style={styles.productMeta}><strong>Estoque processado:</strong> {sale.stockProcessed ? 'Sim' : 'Não'}</p>
                            <div style={styles.productMeta}>
                              <strong>ID Financeiro:</strong>
                              {Array.isArray(sale.idFinance) && sale.idFinance.length > 0 ? (
                                <ol style={{ margin: '6px 0 0 18px', paddingLeft: '8px' }}>
                                  {sale.idFinance.map((financeId: string, financeIndex: number) => (
                                    <li key={`${sale.id || index}-finance-${financeIndex}`} style={{ marginBottom: '2px' }}>
                                      {financeId}
                                    </li>
                                  ))}
                                </ol>
                              ) : (
                                <span> N/A</span>
                              )}
                            </div>
                            <div style={styles.productMeta}>
                              <strong>Métodos de pagamento:</strong>
                              {Array.isArray(sale.payMethod) && sale.payMethod.length > 0 ? (
                                <ol style={{ margin: '6px 0 0 18px', paddingLeft: '8px' }}>
                                  {sale.payMethod.map((methodMap: Record<string, number>, methodIndex: number) => (
                                    <li key={`${sale.id || index}-method-${methodIndex}`} style={{ marginBottom: '2px' }}>
                                      {Object.entries(methodMap)
                                        .map(([method, amount]) => `${method}: ${formatCurrency(amount)}`)
                                        .join(', ')}
                                    </li>
                                  ))}
                                </ol>
                              ) : (
                                <span> N/A</span>
                              )}
                            </div>

                            <p style={styles.productMeta}><strong>Carrinho ID:</strong> {cart.id || 'N/A'}</p>
                            <p style={styles.productMeta}><strong>Total do carrinho:</strong> {formatCurrency(cart.totalValue)}</p>
                            <p style={styles.productMeta}><strong>Desconto total:</strong> {formatCurrency(cart.totalDiscount)}</p>
                            <p style={styles.productMeta}><strong>Qtd. total de produtos:</strong> {cart.qntTotalProduct ?? 'N/A'}</p>
                            <p style={styles.productMeta}><strong>Data do carrinho:</strong> {formatOptionalDate(cart.date)}</p>

                            <p style={styles.productMeta}><strong>Cliente:</strong> {costumer.name || costumer.businessName || 'N/A'}</p>
                            <p style={styles.productMeta}><strong>Telefone cliente:</strong> {costumer.phone || 'N/A'}</p>

                            <p style={styles.productMeta}><strong>Itens da venda:</strong> {cartItems.length}</p>
                            {cartItems.length > 0 && (
                              <ul style={{ margin: '4px 0 0 22px', paddingLeft: '12px', listStyleType: 'disc' }}>
                                {cartItems.map((item: any, itemIndex: number) => (
                                  <li key={`${sale.id || index}-item-${itemIndex}`} style={{ marginBottom: '4px' }}>
                                    <span style={styles.productMeta}>
                                      <strong>{getSaleItemTitle(item, itemIndex)}:</strong>{' '}
                                      qnt {item.qnt ?? item.quantity ?? 'N/A'} | total {formatCurrency(item.totalValue)} | desc. {formatCurrency(item.discount)} | un. {formatCurrency(item.product?.price ?? item.price ?? item.value)}
                                    </span>
                                  </li>
                                ))}
                              </ul>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            ) : panelMode === 'serviceOrders' ? (
              <div style={styles.rightPanelContent}>
                <div style={styles.rightPanelHeader}>
                  {isMobile && (
                    <button
                      type="button"
                      onClick={() => setPanelMode('empty')}
                      style={styles.panelBackButton}
                      title="Voltar"
                    >
                      <ArrowBackIcon style={{ fontSize: 18 }} />
                    </button>
                  )}
                  <h3 style={styles.rightPanelSectionTitle}>Serviços / OS ({serviceOrdersDetails.length})</h3>
                </div>
                {panelLoading ? (
                  <p style={styles.rightPanelStateText}>Carregando ordens de serviço...</p>
                ) : panelError ? (
                  <p style={styles.rightPanelErrorText}>{panelError}</p>
                ) : serviceOrdersDetails.length === 0 ? (
                  <p style={styles.rightPanelStateText}>Sem ordens de serviço cadastradas</p>
                ) : (
                  <div style={styles.productList}>
                    {serviceOrdersDetails.map((order, index) => {
                      const services = Array.isArray(order.services) ? order.services : [];
                      const products = Array.isArray(order.products) ? order.products : [];
                      const client = order?.client || {};
                      const servicesTotal = services.reduce((sum: number, item: any) => sum + (Number(item?.totalValue) || 0), 0);
                      const productsTotal = products.reduce((sum: number, item: any) => sum + (Number(item?.totalValue) || 0), 0);
                      const orderTotal = typeof order?.totalValue === 'number' ? order.totalValue : servicesTotal + productsTotal;
                      const productsQty = typeof order?.qntTotalProduct === 'number'
                        ? order.qntTotalProduct
                        : products.reduce((sum: number, item: any) => sum + (Number(item?.qnt) || Number(item?.quantity) || 0), 0);

                      return (
                        <div key={order.id || index} style={styles.productItem}>
                          <div style={styles.productTopRow}>
                            <div style={styles.productThumbPlaceholder}>🛠️</div>
                            <div style={styles.productMainInfo}>
                              <p style={styles.productName}>
                                OS para {client.name || client.businessName || 'Cliente Anônimo'}
                              </p>
                              <p style={styles.productPrice}>{formatCurrency(orderTotal)}</p>
                            </div>
                          </div>

                          <div style={styles.productMetaGrid}>
                            <p style={styles.productMeta}><strong>ID:</strong> {order.id || 'N/A'}</p>
                            <p style={styles.productMeta}><strong>Status:</strong> {order.status || 'N/A'}</p>
                            <p style={styles.productMeta}><strong>Data:</strong> {formatOptionalDate(order.date || order.createdAt)}</p>
                            <p style={styles.productMeta}><strong>Criado em:</strong> {formatOptionalDate(order.createdAt)}</p>
                            <p style={styles.productMeta}><strong>Atualizado em:</strong> {formatOptionalDate(order.updatedAt)}</p>
                            <p style={styles.productMeta}><strong>Finalizado em:</strong> {formatOptionalDate(order.finishedAt)}</p>
                            <p style={styles.productMeta}><strong>Cancelado em:</strong> {formatOptionalDate(order.canceledAt)}</p>
                            <p style={styles.productMeta}><strong>ID Carrinho:</strong> {order.cartId || 'N/A'}</p>
                            <p style={styles.productMeta}><strong>Total da OS:</strong> {formatCurrency(orderTotal)}</p>
                            <p style={styles.productMeta}><strong>Total serviços:</strong> {formatCurrency(servicesTotal)}</p>
                            <p style={styles.productMeta}><strong>Total produtos:</strong> {formatCurrency(productsTotal)}</p>
                            <p style={styles.productMeta}><strong>Qtd. total de produtos:</strong> {productsQty}</p>
                            <p style={styles.productMeta}><strong>Estoque processado:</strong> {order.stockProcessed ? 'Sim' : 'Não'}</p>
                            <p style={styles.productMeta}><strong>Cliente:</strong> {client.name || client.businessName || 'N/A'}</p>
                            <p style={styles.productMeta}><strong>Telefone cliente:</strong> {client.phone || 'N/A'}</p>
                            <p style={styles.productMeta}><strong>Observações:</strong> {order.observations || 'N/A'}</p>

                            <div style={styles.productMeta}>
                              <strong>ID Financeiro:</strong>
                              {Array.isArray(order.idFinance) && order.idFinance.length > 0 ? (
                                <ol style={{ margin: '6px 0 0 18px', paddingLeft: '8px' }}>
                                  {order.idFinance.map((financeId: string, financeIndex: number) => (
                                    <li key={`${order.id || index}-finance-${financeIndex}`} style={{ marginBottom: '2px' }}>
                                      {financeId}
                                    </li>
                                  ))}
                                </ol>
                              ) : (
                                <span> N/A</span>
                              )}
                            </div>

                            <div style={styles.productMeta}>
                              <strong>Métodos de pagamento:</strong>
                              {Array.isArray(order.payMethod) && order.payMethod.length > 0 ? (
                                <ol style={{ margin: '6px 0 0 18px', paddingLeft: '8px' }}>
                                  {order.payMethod.map((methodMap: Record<string, number>, methodIndex: number) => (
                                    <li key={`${order.id || index}-method-${methodIndex}`} style={{ marginBottom: '2px' }}>
                                      {Object.entries(methodMap || {})
                                        .map(([method, amount]) => `${method}: ${formatCurrency(Number(amount) || 0)}`)
                                        .join(', ')}
                                    </li>
                                  ))}
                                </ol>
                              ) : (
                                <span> N/A</span>
                              )}
                            </div>

                            <p style={styles.productMeta}><strong>Serviços da OS:</strong> {services.length}</p>
                            {services.length > 0 && (
                              <ul style={{ margin: '4px 0 0 22px', paddingLeft: '12px', listStyleType: 'disc' }}>
                                {services.map((serviceItem: any, serviceIndex: number) => (
                                  <li key={`${order.id || index}-service-${serviceIndex}`} style={{ marginBottom: '4px' }}>
                                    <span style={styles.productMeta}>
                                      <strong>{getServiceItemTitle(serviceItem, serviceIndex)}:</strong>{' '}
                                      qnt {serviceItem.quantity ?? serviceItem.qnt ?? 1} | total {formatCurrency(serviceItem.totalValue)} | un. {formatCurrency(serviceItem.price ?? serviceItem.service?.price ?? serviceItem.service?.value ?? serviceItem.value ?? serviceItem.unitValue)}
                                    </span>
                                  </li>
                                ))}
                              </ul>
                            )}

                            <p style={styles.productMeta}><strong>Produtos da OS:</strong> {products.length}</p>
                            {products.length > 0 && (
                              <ul style={{ margin: '4px 0 0 22px', paddingLeft: '12px', listStyleType: 'disc' }}>
                                {products.map((item: any, itemIndex: number) => (
                                  <li key={`${order.id || index}-item-${itemIndex}`} style={{ marginBottom: '4px' }}>
                                    <span style={styles.productMeta}>
                                      <strong>{getSaleItemTitle(item, itemIndex)}:</strong>{' '}
                                      qnt {item.qnt ?? item.quantity ?? 'N/A'} | total {formatCurrency(item.totalValue)} | desc. {formatCurrency(item.discount)} | un. {formatCurrency(item.product?.price ?? item.price ?? item.value)}
                                    </span>
                                  </li>
                                ))}
                              </ul>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            ) : panelMode === 'costumers' ? (
              <div style={styles.rightPanelContent}>
                <div style={styles.rightPanelHeader}>
                  {isMobile && (
                    <button
                      type="button"
                      onClick={() => setPanelMode('empty')}
                      style={styles.panelBackButton}
                      title="Voltar"
                    >
                      <ArrowBackIcon style={{ fontSize: 18 }} />
                    </button>
                  )}
                  <h3 style={styles.rightPanelSectionTitle}>Clientes ({costumersDetails.length})</h3>
                </div>
                {panelLoading ? (
                  <p style={styles.rightPanelStateText}>Carregando clientes...</p>
                ) : panelError ? (
                  <p style={styles.rightPanelErrorText}>{panelError}</p>
                ) : costumersDetails.length === 0 ? (
                  <p style={styles.rightPanelStateText}>Sem clientes cadastrados</p>
                ) : (
                  <div style={styles.productList}>
                    {costumersDetails.map((costumer, index) => {
                      const knownKeys = [
                        'id',
                        'name',
                        'businessName',
                        'customerName',
                        'mail',
                        'email',
                        'phone',
                        'document',
                        'cpf',
                        'cnpj',
                        'photoUrl',
                        'pathPhoto',
                        'path_photo',
                        'avatar',
                        'createdAt',
                        'updatedAt',
                        'address',
                      ];

                      const extraEntries = Object.entries(costumer || {}).filter(
                        ([key]) => !knownKeys.includes(key)
                      );

                      const displayName =
                        costumer.name || costumer.businessName || costumer.customerName || 'Sem nome';
                      const displayPhoto = costumer.photoUrl || costumer.pathPhoto || costumer.path_photo || costumer.avatar || null;

                      return (
                        <div key={costumer.id || index} style={styles.productItem}>
                          <div style={styles.productTopRow}>
                            {displayPhoto ? (
                              <img src={displayPhoto} alt={displayName} style={styles.productThumb} />
                            ) : (
                              <div style={styles.productThumbPlaceholder}>👤</div>
                            )}
                            <div style={styles.productMainInfo}>
                              <p style={styles.productName}>{displayName}</p>
                              <p style={styles.productPrice}>{costumer.phone || costumer.mail || costumer.email || 'N/A'}</p>
                            </div>
                          </div>

                          <div style={styles.productMetaGrid}>
                            <p style={styles.productMeta}><strong>ID:</strong> {costumer.id || 'N/A'}</p>
                            <p style={styles.productMeta}><strong>Email:</strong> {costumer.mail || costumer.email || 'N/A'}</p>
                            <p style={styles.productMeta}><strong>Telefone:</strong> {costumer.phone || 'N/A'}</p>
                            <p style={styles.productMeta}><strong>Documento:</strong> {costumer.document || costumer.cpf || costumer.cnpj || 'N/A'}</p>
                            <p style={styles.productMeta}><strong>Endereço:</strong> {formatFieldValue('address', costumer.address)}</p>
                            <p style={styles.productMeta}><strong>Criado em:</strong> {formatOptionalDate(costumer.createdAt)}</p>
                            <p style={styles.productMeta}><strong>Atualizado em:</strong> {formatOptionalDate(costumer.updatedAt)}</p>
                            {extraEntries.map(([key, value]) => (
                              <p key={`${costumer.id || index}-${key}`} style={styles.productMeta}>
                                <strong>{formatFieldLabel(key)}:</strong> {formatFieldValue(key, value)}
                              </p>
                            ))}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            ) : (
              <div style={styles.rightPanelEmpty}>
                <span style={styles.rightPanelEmptyIcon}>📭</span>
                <p style={styles.rightPanelEmptyText}>Sem Registro(s)</p>
              </div>
            )}
          </aside>
          )}
        </div>
      </div>

      {/* Modal de Confirmação */}
      {showSaleDeleteModal && saleToDelete && (
        <div style={styles.modalOverlay} onClick={() => !deletingSale && setShowSaleDeleteModal(false)}>
          <div style={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <div style={styles.modalHeader}>
              <WarningIcon style={{ fontSize: 48, color: '#d32f2f' }} />
              <h2 style={styles.modalTitle}>Excluir Venda</h2>
            </div>
            <div style={styles.modalBody}>
              <p style={styles.modalText}>
                <strong>Atenção!</strong> Esta venda será <strong>permanentemente excluída</strong>.
              </p>
              <p style={styles.modalText}>
                Venda selecionada: <strong>{saleToDelete.cart?.costumer?.name || saleToDelete.cart?.costumer?.businessName || 'Cliente Anônimo'}</strong>
              </p>
              <p style={styles.modalText}>
                Ao confirmar, os registros financeiros vinculados também serão removidos quando existirem.
              </p>
            </div>
            <div style={styles.modalFooter}>
              <button
                onClick={() => setShowSaleDeleteModal(false)}
                disabled={deletingSale}
                style={styles.modalCancelButton}
              >
                Cancelar
              </button>
              <button
                onClick={handleDeleteSale}
                disabled={deletingSale}
                style={styles.modalDeleteButton}
              >
                {deletingSale ? 'Excluindo...' : 'Sim, Excluir Venda'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showDeleteModal && (
        <div style={styles.modalOverlay} onClick={() => !deleting && setShowDeleteModal(false)}>
          <div style={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <div style={styles.modalHeader}>
              <WarningIcon style={{ fontSize: 48, color: '#d32f2f' }} />
              <h2 style={styles.modalTitle}>Confirmar Exclusão</h2>
            </div>
            <div style={styles.modalBody}>
              <p style={styles.modalText}>
                <strong>Atenção!</strong> Esta ação é <strong>irreversível</strong>.
              </p>
              <p style={styles.modalText}>
                Ao confirmar, os seguintes dados serão <strong>permanentemente excluídos</strong>:
              </p>
              <ul style={styles.modalList}>
                <li>Todos os clientes cadastrados</li>
                <li>Todos os produtos e fotos</li>
                <li>Todas as vendas realizadas</li>
                <li>Todos os métodos de pagamento</li>
                <li>Todos os dispositivos vinculados</li>
                <li>Conta do usuário</li>
              </ul>
              <p style={styles.modalTextBold}>
                Tem certeza que deseja excluir a conta de <strong>{user.businessName}</strong>?
              </p>
            </div>
            <div style={styles.modalFooter}>
              <button
                onClick={() => setShowDeleteModal(false)}
                disabled={deleting}
                style={styles.modalCancelButton}
              >
                Cancelar
              </button>
              <button
                onClick={handleDeleteUser}
                disabled={deleting}
                style={styles.modalDeleteButton}
              >
                {deleting ? 'Excluindo...' : 'Sim, Excluir Permanentemente'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const styles: { [key: string]: React.CSSProperties } = {
  container: {
    height: '100vh',
    background: '#f5f5f5',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
  },
  header: {
    background: theme.colors.white,
    padding: '25px 40px',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
  },
  title: {
    color: theme.colors.primary,
    margin: 0,
    fontSize: '28px',
    fontWeight: 'bold',
  },
  headerContent: {
    display: 'flex',
    alignItems: 'center',
    gap: '20px',
  },
  backButton: {
    padding: '12px',
    borderRadius: '8px',
    border: `2px solid ${theme.colors.primary}`,
    background: 'transparent',
    color: theme.colors.primary,
    fontSize: '24px',
    cursor: 'pointer',
    transition: 'all 0.3s',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minWidth: '48px',
    minHeight: '48px',
  },
  content: {
    padding: '24px 40px 32px',
    display: 'flex',
    justifyContent: 'flex-start',
    flex: 1,
    minHeight: 0,
  },
  contentMobile: {
    padding: '16px',
  },
  detailLayout: {
    width: '100%',
    display: 'flex',
    gap: '24px',
    alignItems: 'stretch',
    flexWrap: 'nowrap',
    minHeight: 0,
  },
  detailLayoutMobile: {
    gap: 0,
  },
  leftColumn: {
    flex: '0 1 540px',
    minWidth: '320px',
    maxHeight: '100%',
    overflowY: 'auto',
    paddingRight: '4px',
  },
  leftColumnMobile: {
    flex: '1 1 auto',
    minWidth: 0,
    maxHeight: 'none',
    paddingRight: 0,
  },
  card: {
    background: theme.colors.white,
    padding: '40px',
    borderRadius: '12px',
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
    maxWidth: '540px',
    width: '100%',
  },
  rightPanel: {
    flex: '1 1 320px',
    minWidth: '280px',
    background: theme.colors.white,
    borderRadius: '12px',
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
    padding: '24px',
    alignSelf: 'stretch',
    minHeight: '100%',
    maxHeight: '100%',
    overflowY: 'auto',
  },
  rightPanelMobile: {
    flex: '1 1 auto',
    minWidth: 0,
    maxHeight: 'none',
  },
  rightPanelEmpty: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    textAlign: 'center',
    gap: '12px',
  },
  rightPanelEmptyIcon: {
    fontSize: '48px',
    lineHeight: 1,
  },
  rightPanelEmptyText: {
    margin: 0,
    color: theme.colors.secondary,
    fontSize: '18px',
    fontWeight: '600',
  },
  rightPanelContent: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
  },
  rightPanelHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
  },
  panelBackButton: {
    border: `1px solid ${theme.colors.tertiary}`,
    background: theme.colors.white,
    color: theme.colors.primary,
    borderRadius: '8px',
    width: '34px',
    height: '34px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    flexShrink: 0,
  },
  rightPanelSectionTitle: {
    margin: 0,
    color: theme.colors.primary,
    fontSize: '22px',
    fontWeight: 'bold',
  },
  rightPanelStateText: {
    margin: 0,
    color: theme.colors.secondary,
    fontSize: '14px',
  },
  rightPanelErrorText: {
    margin: 0,
    color: theme.colors.error,
    fontSize: '14px',
    fontWeight: 'bold',
  },
  productList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '14px',
  },
  productItem: {
    background: '#f9f9f9',
    borderRadius: '10px',
    padding: '12px',
    borderLeft: `4px solid ${theme.colors.secondary}`,
    display: 'flex',
    flexDirection: 'column',
    gap: '10px',
  },
  productTopRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  },
  saleTopRow: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: '12px',
  },
  productThumb: {
    width: '56px',
    height: '56px',
    borderRadius: '8px',
    objectFit: 'cover',
    border: `1px solid ${theme.colors.tertiary}`,
  },
  productThumbPlaceholder: {
    width: '56px',
    height: '56px',
    borderRadius: '8px',
    background: '#e8eef7',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '22px',
  },
  productMainInfo: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
  },
  productName: {
    margin: 0,
    color: theme.colors.primary,
    fontSize: '16px',
    fontWeight: 'bold',
  },
  productPrice: {
    margin: 0,
    color: theme.colors.text,
    fontSize: '14px',
    fontWeight: '600',
  },
  productMetaGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr',
    gap: '4px',
  },
  productMeta: {
    margin: 0,
    color: theme.colors.text,
    fontSize: '13px',
    lineHeight: '1.4',
    wordBreak: 'break-word',
  },
  deleteSaleButton: {
    border: `1px solid ${theme.colors.tertiary}`,
    background: theme.colors.white,
    color: theme.colors.error,
    borderRadius: '8px',
    width: '34px',
    height: '34px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    flexShrink: 0,
  },
  avatarContainer: {
    display: 'flex',
    justifyContent: 'center',
    marginBottom: '20px',
  },
  avatar: {
    width: '120px',
    height: '120px',
    borderRadius: '50%',
    background: theme.gradient,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    fontSize: '48px',
    color: theme.colors.white,
    fontWeight: 'bold',
    border: `4px solid ${theme.colors.white}`,
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.2)',
  },
  avatarImage: {
    width: '120px',
    height: '120px',
    borderRadius: '50%',
    objectFit: 'cover',
    border: `4px solid ${theme.colors.tertiary}`,
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.2)',
  },
  userName: {
    textAlign: 'center',
    color: theme.colors.primary,
    fontSize: '32px',
    marginBottom: '10px',
  },
  userId: {
    textAlign: 'center',
    color: theme.colors.secondary,
    fontSize: '14px',
    marginBottom: '5px',
    fontFamily: 'monospace',
  },
  userUid: {
    textAlign: 'center',
    color: theme.colors.tertiary,
    fontSize: '12px',
    marginBottom: '20px',
    fontFamily: 'monospace',
    opacity: 0.8,
  },
  statusBadge: {
    textAlign: 'center',
    marginBottom: '30px',
  },
  statusActive: {
    display: 'inline-block',
    padding: '10px 20px',
    borderRadius: '20px',
    background: '#e8f5e9',
    color: '#2e7d32',
    fontSize: '16px',
    fontWeight: 'bold',
  },
  statusExpired: {
    display: 'inline-block',
    padding: '10px 20px',
    borderRadius: '20px',
    background: '#ffebee',
    color: '#c62828',
    fontSize: '16px',
    fontWeight: 'bold',
  },
  infoSection: {
    display: 'flex',
    flexDirection: 'column',
    gap: '15px',
    marginBottom: '30px',
  },
  infoItem: {
    display: 'flex',
    flexDirection: 'column',
    gap: '5px',
    padding: '15px',
    background: '#f9f9f9',
    borderRadius: '8px',
    borderLeft: `4px solid ${theme.colors.secondary}`,
  },
  statHeaderRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '12px',
  },
  detailIconButton: {
    border: 'none',
    background: theme.colors.secondary,
    color: theme.colors.white,
    borderRadius: '6px',
    width: '30px',
    height: '30px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
  },
  financeCountersRow: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
    gap: '10px',
    width: '100%',
  },
  financeCounterChip: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    background: '#eef4fb',
    borderRadius: '8px',
    padding: '8px 10px',
    border: `1px solid ${theme.colors.tertiary}`,
  },
  financeCounterLabel: {
    color: theme.colors.secondary,
    fontSize: '13px',
    fontWeight: '600',
  },
  financeCounterValue: {
    color: theme.colors.primary,
    fontSize: '16px',
    fontWeight: '700',
    marginLeft: 'auto',
  },
  financeCounterValueMobile: {
    color: theme.colors.primary,
    fontSize: '18px',
    fontWeight: '700',
    marginLeft: 'auto',
  },
  financePayableIcon: {
    fontSize: '18px',
    color: '#f44336',
  },
  financeReceivableIcon: {
    fontSize: '18px',
    color: '#4caf50',
  },
  label: {
    fontSize: '14px',
    color: theme.colors.secondary,
    fontWeight: 'bold',
  },
  value: {
    fontSize: '16px',
    color: theme.colors.text,
  },
  sectionTitle: {
    color: theme.colors.primary,
    fontSize: '20px',
    marginBottom: '15px',
    fontWeight: 'bold',
  },
  dateEditContainer: {
    width: '100%',
  },
  dateDisplayBox: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    width: '100%',
  },
  editButton: {
    padding: '10px',
    borderRadius: '6px',
    border: 'none',
    background: theme.colors.secondary,
    color: theme.colors.white,
    fontSize: '20px',
    cursor: 'pointer',
    transition: 'background 0.3s',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minWidth: '40px',
    minHeight: '40px',
  },
  editDateBox: {
    display: 'flex',
    flexDirection: 'column',
    gap: '10px',
    width: '100%',
  },
  dateInput: {
    padding: '10px',
    borderRadius: '6px',
    border: `2px solid ${theme.colors.tertiary}`,
    fontSize: '14px',
    outline: 'none',
  },
  editButtons: {
    display: 'flex',
    gap: '10px',
  },
  saveButton: {
    flex: 1,
    padding: '10px',
    borderRadius: '6px',
    border: 'none',
    background: '#4caf50',
    color: theme.colors.white,
    fontSize: '14px',
    fontWeight: 'bold',
    cursor: 'pointer',
  },
  cancelButton: {
    flex: 1,
    padding: '10px',
    borderRadius: '6px',
    border: 'none',
    background: '#f44336',
    color: theme.colors.white,
    fontSize: '14px',
    fontWeight: 'bold',
    cursor: 'pointer',
  },
  deviceCard: {
    background: '#e3f2fd',
    padding: '15px',
    borderRadius: '8px',
    marginBottom: '15px',
    borderLeft: `4px solid ${theme.colors.tertiary}`,
  },
  deviceHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '10px',
    paddingBottom: '10px',
    borderBottom: `1px solid ${theme.colors.tertiary}`,
  },
  deviceNumber: {
    fontSize: '16px',
    fontWeight: 'bold',
    color: theme.colors.primary,
  },
  deleteDeviceButton: {
    padding: '8px',
    borderRadius: '6px',
    border: 'none',
    background: '#f44336',
    color: 'white',
    fontSize: '20px',
    cursor: 'pointer',
    transition: 'all 0.3s',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minWidth: '36px',
    minHeight: '36px',
  },
  deviceInfo: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  deviceRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  deviceLabel: {
    fontSize: '13px',
    color: theme.colors.secondary,
    fontWeight: 'bold',
  },
  deviceValue: {
    fontSize: '14px',
    color: theme.colors.text,
    fontFamily: 'monospace',
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: '100vh',
    background: theme.gradient,
  },
  loading: {
    color: theme.colors.white,
    fontSize: '24px',
    fontWeight: 'bold',
  },
  errorContainer: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: '100vh',
    gap: '20px',
  },
  error: {
    color: theme.colors.error,
    fontSize: '20px',
  },
  errorBackButton: {
    padding: '12px 24px',
    borderRadius: '8px',
    border: 'none',
    background: theme.gradient,
    color: theme.colors.white,
    fontSize: '16px',
    fontWeight: 'bold',
    cursor: 'pointer',
    transition: 'transform 0.2s',
  },
  dangerZone: {
    marginTop: '40px',
    padding: '24px',
    background: '#ffebee',
    borderRadius: '12px',
    border: '2px solid #ef5350',
  },
  dangerZoneTitle: {
    color: '#c62828',
    fontSize: '20px',
    marginBottom: '12px',
    fontWeight: 'bold',
  },
  dangerZoneDescription: {
    color: '#d32f2f',
    fontSize: '14px',
    marginBottom: '20px',
    lineHeight: '1.6',
  },
  deleteAccountButton: {
    padding: '14px 28px',
    borderRadius: '8px',
    border: 'none',
    background: '#d32f2f',
    color: 'white',
    fontSize: '16px',
    fontWeight: 'bold',
    cursor: 'pointer',
    transition: 'all 0.3s',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '100%',
  },
  modalOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'rgba(0, 0, 0, 0.7)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 1000,
  },
  modalContent: {
    background: 'white',
    borderRadius: '16px',
    padding: '32px',
    maxWidth: '550px',
    width: '90%',
    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
  },
  modalHeader: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    marginBottom: '24px',
  },
  modalTitle: {
    color: '#d32f2f',
    fontSize: '28px',
    fontWeight: 'bold',
    marginTop: '12px',
    marginBottom: 0,
  },
  modalBody: {
    marginBottom: '24px',
  },
  modalText: {
    color: '#333',
    fontSize: '15px',
    marginBottom: '12px',
    lineHeight: '1.6',
  },
  modalTextBold: {
    color: '#d32f2f',
    fontSize: '16px',
    fontWeight: 'bold',
    marginTop: '20px',
    marginBottom: 0,
  },
  modalList: {
    marginLeft: '24px',
    marginTop: '12px',
    marginBottom: '12px',
    color: '#555',
    fontSize: '14px',
    lineHeight: '1.8',
  },
  modalFooter: {
    display: 'flex',
    gap: '16px',
    justifyContent: 'flex-end',
  },
  modalCancelButton: {
    padding: '12px 24px',
    borderRadius: '8px',
    border: '2px solid #757575',
    background: 'white',
    color: '#757575',
    fontSize: '16px',
    fontWeight: 'bold',
    cursor: 'pointer',
    transition: 'all 0.3s',
  },
  modalDeleteButton: {
    padding: '12px 24px',
    borderRadius: '8px',
    border: 'none',
    background: '#d32f2f',
    color: 'white',
    fontSize: '16px',
    fontWeight: 'bold',
    cursor: 'pointer',
    transition: 'all 0.3s',
  },
};

export default UserDetail;